import React, { useEffect, useState } from 'react'
import { Button, Checkbox, Divider, Form, Input, Select, Space, Spin, Tooltip } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Box, LocationSelectionWrapper, MapWrapper, OptionsBox, OptionsWrapper, SelectorWrapper } from '../segment.styled'
import MapMarker from './MapMarker'
import { MapContainer, TileLayer } from 'react-leaflet'
import useParams from '../../../hook/useParams'
import { fetchLocationByIds, fetchLocations, fetchLocationTags } from '../../../apis/location'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

const initParams = {
  maxResults: 10,
  sort: '-updatedAt,id',
  startIndex: 0,
  geoTranslate: 2,
  status: 1,
  searchType: 'tag',
  nextPage: false
}
function LocationCriteria (props) {
  const { defConfig, defPlaceholder, form } = props
  const { t: ln_t } = useTranslation()
  const user = useSelector((state) => state.user)
  const { nowBrand } = user || {}
  const params = useParams({ ...initParams, brandId: nowBrand })
  const { fetchParams, handleFetchParams } = params
  const location = useLocation()
  const { state } = location || {}
  const { data } = state || {}

  const [position, setPosition] = useState({ lat: 23.723876, lng: 120.872141 })
  const [selectedLocation, setSelectedLocation] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [locationData, setLocationData] = useState([])
  const [totalData, setTotalData] = useState(0)
  const [tags, setTags] = useState([])
  const [searchType, setSearchType] = useState(initParams.searchType)
  const [indeterminate, setIndeterminate] = useState(false)
  const [checkAll, setCheckAll] = useState(false)

  const locTypeOpt = [
    { value: 'visitor', label: ln_t('criteria.location.visitor') },
    { value: 'home', label: ln_t('criteria.location.home') },
    { value: 'work', label: ln_t('criteria.location.work') }
  ]
  const searchTypeOpt = [{ value: 'tag', label: ln_t('criteria.location.tag') }, { value: 'name', label: ln_t('criteria.location.name') }]

  const handleSelectLoc = (list) => {
    const selected = locationData?.filter(i => list.includes(i.value))
    form.setFieldValue('locations', selected.length ? selected : undefined)
    setSelectedLocation(selected)
    setIndeterminate(!!list.length && list.length < locationData.length)
    setCheckAll(list.length === locationData.length)
  }
  const handleSelectAll = (e) => {
    setSelectedLocation(e.target.checked ? locationData : [])
    setIndeterminate(false)
    setCheckAll(e.target.checked)
  }

  const formatLocData = (data = []) => {
    const formatTag = tags => tags.split('@').join(', ')
    return data?.map(i => ({
      ...i,
      value: i.id,
      label: <Tooltip placement="right" title={i.tags ? `${ln_t('criteria.location.tag')}: ${formatTag(i.tags)}` : ln_t('criteria.location.noTag')}>{i.name}</Tooltip>
    }))
  }
  const getLocations = async () => {
    setIsLoading(true)
    const { nextPage, ...apiParams } = fetchParams
    const loc = await fetchLocations(apiParams)
    if (loc?.status === 200) {
      setLocationData([
        ...nextPage ? locationData : [],
        ...formatLocData(loc?.data)
      ])
      const count = loc.headers?.['x-content-record-total'] || 0
      setTotalData(count)
    }
    setIsLoading(false)
  }
  const getTags = async () => {
    const tag = await fetchLocationTags({ maxResults: 999, brandId: nowBrand })
    if (tag?.status === 200) setTags(tag?.data?.map(({ name }) => ({ value: name, label: name })))
  }
  const handleEditMode = async () => {
    const { code, points } = data?.rule || {}
    const ids = points?.map(({ id }) => id)?.join(',')
    const res = await fetchLocationByIds(ids)
    const loc = formatLocData(res?.data)
    form.setFieldsValue({ code, locations: loc || points })
    setSelectedLocation(loc || [])
  }
  useEffect(() => {
    getTags()
    getLocations()
    if (data) handleEditMode()
  }, [])
  useEffect(() => { getLocations() }, [fetchParams])

  return (
    <>
      <Form.Item {...defConfig('code', 'select')}>
        <Select showSearch placeholder={defPlaceholder('code', 'select')} options={locTypeOpt} optionFilterProp="children" />
      </Form.Item>
      <LocationSelectionWrapper>
        <MapWrapper>
          <MapContainer center={position} zoom={7}>
            <TileLayer
              attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapMarker position={position} setPosition={setPosition} selectedLocation={selectedLocation} />
          </MapContainer>
        </MapWrapper>

        <SelectorWrapper>
          <Space.Compact style={{ width: '100%' }}>
            <Select style={{ width: '100px' }} value={searchType} onChange={e => setSearchType(e)} options={searchTypeOpt}/>
            {searchType === 'tag'
              ? <>
                  <Select
                    showSearch
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder={defPlaceholder('location.tag', 'input')}
                    options={tags}
                    optionFilterProp="children"
                    onChange={e => handleFetchParams('add', { ...initParams, searchType, searchKeyword: e?.join(',') })}
                    />
                  <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => handleFetchParams('add', {})} />
                </>
              : <Input.Search
                  placeholder={defPlaceholder('location.name', 'input')}
                  enterButton={<Button type="primary" size="small" icon={<SearchOutlined />} />}
                  onSearch={e => handleFetchParams('add', { ...initParams, searchType, searchKeyword: e })}
                  onKeyPress={e => { if (e.key === 'Enter') e.preventDefault() }} // to prevent submitting form on enter
                />
            }
          </Space.Compact>
          <OptionsWrapper>
            <OptionsBox>
              <div className="title">{ln_t('criteria.location.list')} :</div>
              <Box>
                {isLoading
                  ? <Spin className="d-flex justify-content-center mt-5" />
                  : <>
                      <Checkbox indeterminate={indeterminate} onChange={handleSelectAll} checked={checkAll}>{ln_t('criteria.location.selectAll')}</Checkbox>
                      <Checkbox.Group value={selectedLocation?.map(i => i.value)} options={locationData} onChange={handleSelectLoc} />
                      {fetchParams.startIndex + fetchParams.maxResults >= totalData
                        ? <Divider style={{ padding: '2px 8px', color: 'rgba(0,0,0,.25)' }} plain>{ln_t('criteria.location.allLoaded')}</Divider>
                        : <Button size="small" type="link" style={{ margin: '8px 2px 0px' }} onClick={() => handleFetchParams('add', { startIndex: fetchParams.startIndex + fetchParams.maxResults, nextPage: true })}>
                            {ln_t('criteria.location.loadMore')}
                          </Button>
                      }
                    </>
                }
              </Box>
            </OptionsBox>
            <OptionsBox>
              <div className="title">{ln_t('criteria.location.selectedLocation')} :</div>
              <Form.Item {...defConfig('locations', 'select')} label="">
                <Box>
                  <Checkbox.Group value={selectedLocation?.map(i => i.value)} options={selectedLocation} onChange={handleSelectLoc} />
                </Box>
              </Form.Item>
            </OptionsBox>
          </OptionsWrapper>
        </SelectorWrapper>

      </LocationSelectionWrapper>
    </>
  )
}

export default LocationCriteria
